import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapsible,
  InlineError,
  Layout,
  LegacyCard,
  Select,
  TextField,
} from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  lookupPaypalTransaction,
  resetPaypalTransactionLookup,
} from "../../../redux/store/paypalTxLookup/Action";
import SuccessBanner from "../../../components/banner/SuccessBanner";
import CriticalBanner from "../../../components/banner/criticalBanner";
import "./style.css";

// PayPal Reporting API transaction_status codes
// https://developer.paypal.com/docs/api/transaction-search/v1/
const STATUS_LABELS = {
  S: "Success",
  V: "Reversed",
  P: "Pending",
  D: "Denied",
  F: "Partially Refunded",
};

const formatStatus = (code) =>
  code ? `${STATUS_LABELS[code] || "Unknown"} (${code})` : "—";

const formatAmount = (amount) => {
  if (!amount) return "—";
  const value = amount.value ?? "";
  const cur = amount.currency_code ?? "";
  return value || cur ? `${value} ${cur}`.trim() : "—";
};

const Row = ({ label, value }) =>
  value === undefined || value === null || value === "" ? null : (
    <tr>
      <td style={{ padding: "4px 12px 4px 0", color: "#6d7175", verticalAlign: "top" }}>
        {label}
      </td>
      <td style={{ padding: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {value}
      </td>
    </tr>
  );

const TxSummary = ({ details, merchantAccountId }) => {
  const info = details?.transaction_info || {};
  const payer = details?.payer_info || {};
  const ship = details?.shipping_info || {};
  const payerName = payer.payer_name?.alternate_full_name
    ? payer.payer_name.alternate_full_name
    : [payer.payer_name?.given_name, payer.payer_name?.surname].filter(Boolean).join(" ");
  const shipAddrLines = [
    ship.address?.line1,
    ship.address?.line2,
    [ship.address?.city, ship.address?.state, ship.address?.postal_code].filter(Boolean).join(", "),
    ship.address?.country_code,
  ].filter(Boolean).join("\n");

  return (
    <table style={{ borderCollapse: "collapse", marginTop: 8 }}>
      <tbody>
        <Row label="Merchant PayPal account ID" value={merchantAccountId} />
        <Row label="Transaction ID" value={info.transaction_id} />
        <Row label="Date" value={info.transaction_initiation_date} />
        <Row label="Status" value={formatStatus(info.transaction_status)} />
        <Row label="Event code" value={info.transaction_event_code} />
        <Row label="Amount" value={formatAmount(info.transaction_amount)} />
        <Row label="Fee" value={formatAmount(info.fee_amount)} />
        <Row label="Shipping amount" value={formatAmount(info.shipping_amount)} />
        <Row label="Counterparty PayPal account ID" value={info.paypal_account_id} />
        <Row label="Reference ID" value={info.paypal_reference_id} />
        <Row label="Invoice ID" value={info.invoice_id} />
        <Row label="Custom field" value={info.custom_field} />
        <Row label="Instrument" value={info.instrument_sub_type || info.instrument_type} />
        <Row label="Payer email" value={payer.email_address} />
        <Row label="Payer name" value={payerName} />
        <Row label="Payer country" value={payer.country_code} />
        <Row label="Ship to" value={ship.name} />
        <Row label="Ship address" value={shipAddrLines} />
      </tbody>
    </table>
  );
};

const PaypalTxChecker = () => {
  const { id: shopId } = useParams();
  const dispatch = useDispatch();

  const [merchantAccountId, setMerchantAccountId] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [errors, setErrors] = useState({ merchantId: "", transactionId: "" });
  const [submitted, setSubmitted] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const paypalSettingData = useSelector((s) => s.requriedPaypalSettingData);
  const lookup = useSelector((s) => s.paypalTxLookup);

  const merchantOptions = useMemo(() => {
    const list = paypalSettingData?.data?.content;
    if (!Array.isArray(list)) return [];
    return list
      .filter(Boolean)
      .map((s) => ({ label: s.merchantAccountId, value: s.merchantAccountId }));
  }, [paypalSettingData]);

  // Default to first merchant when settings load
  useEffect(() => {
    if (!merchantAccountId && merchantOptions.length > 0) {
      setMerchantAccountId(merchantOptions[0].value);
    }
  }, [merchantOptions, merchantAccountId]);

  // Reset state when leaving the screen
  useEffect(() => () => dispatch(resetPaypalTransactionLookup()), [dispatch]);

  const handleSubmit = useCallback(() => {
    const next = { merchantId: "", transactionId: "" };
    if (!merchantAccountId) next.merchantId = "Select a merchant account id";
    if (!transactionId.trim()) next.transactionId = "Enter a transaction id";
    setErrors(next);
    if (next.merchantId || next.transactionId) return;

    setSubmitted(true);
    setShowRaw(false);
    dispatch(
      lookupPaypalTransaction({
        shopId: Number(shopId),
        shopMerchantAccountId: merchantAccountId,
        transactionId: transactionId.trim(),
      }),
    );
  }, [dispatch, merchantAccountId, shopId, transactionId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const found = submitted && lookup.isSuccess && lookup.data?.found === true;
  const notFound = submitted && lookup.isSuccess && lookup.data?.found === false;
  const errored = submitted && lookup.isError;

  return (
    <Layout.Section>
      <div className="wrapper">
        <LegacyCard sectioned>
          <div style={{ padding: "15px" }}>
            <LegacyCard sectioned>
              <div
                className="d-flex flex-wrap"
                style={{
                  alignItems: "end",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <div className="textFieldContainer">
                  <Select
                    label="Merchant account ID"
                    options={merchantOptions}
                    onChange={setMerchantAccountId}
                    value={merchantAccountId}
                    placeholder="Select Merchant account id"
                  />
                  {errors.merchantId && (
                    <div style={{ position: "absolute" }}>
                      <InlineError
                        message={errors.merchantId}
                        fieldID="merchantIdField"
                      />
                    </div>
                  )}
                </div>

                <div onKeyDown={handleKeyDown} className="textFieldContainer">
                  <TextField
                    label="Transaction ID"
                    value={transactionId}
                    onChange={setTransactionId}
                    autoComplete="off"
                    placeholder="Please enter Transaction ID"
                  />
                  {errors.transactionId && (
                    <div style={{ position: "absolute" }}>
                      <InlineError
                        message={errors.transactionId}
                        fieldID="transactionIdField"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Button
                    primary
                    loading={lookup.isRequest}
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </div>
              </div>

              {found && (
                <>
                  <SuccessBanner runTime={1} message="Transaction found" />
                  <TxSummary
                    details={lookup.data.details}
                    merchantAccountId={lookup.data.merchantAccountId}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Button
                      plain
                      onClick={() => setShowRaw((s) => !s)}
                      ariaExpanded={showRaw}
                      ariaControls="raw-tx-details"
                    >
                      {showRaw ? "Hide raw response" : "Show raw response"}
                    </Button>
                    <Collapsible open={showRaw} id="raw-tx-details">
                      <pre
                        style={{
                          marginTop: 8,
                          padding: 12,
                          background: "#f6f6f7",
                          borderRadius: 4,
                          maxHeight: 480,
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {JSON.stringify(lookup.data.details, null, 2)}
                      </pre>
                    </Collapsible>
                  </div>
                </>
              )}
              {notFound && (
                <CriticalBanner
                  runTime={1}
                  message={lookup.data?.message || "Not found"}
                />
              )}
              {errored && (
                <CriticalBanner
                  runTime={1}
                  message={lookup.error || "Lookup failed"}
                />
              )}
            </LegacyCard>
          </div>
        </LegacyCard>
      </div>
    </Layout.Section>
  );
};

export default PaypalTxChecker;
